const activeStops = new Map();
const recordingRoles = new Set(['admin', 'super_admin', 'superadmin', 'church_admin', 'staff', 'faculty']);
const roleName = value => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

export const canUseRecordingControls = ({ room, role, participantStatus }) => Boolean(
  room?.roomId && room?.access?.canRecord === true && recordingRoles.has(roleName(role)) &&
  ['admitted', 'presenting'].includes(String(participantStatus || '').toLowerCase()) &&
  room?.isActive !== false && !['ended', 'cancelled', 'deleted'].includes(String(room?.meetingStatus || room?.meetingSatatus || '').toLowerCase())
);

export const recordingUiError = error => {
  const message = String(error?.response?.data?.error || error?.message || 'Recording could not be saved. Please retry.');
  if (/idrive|spacebyte|bunny|\bs3\b|access.?key|secret|bucket|signature|https?:\/\//i.test(message)) {
    return 'Recording could not be saved. Please retry or ask an administrator to check the recording setup.';
  }
  return message.slice(0, 500);
};

export const notifyRecordingStop = (roomId, outcome) => {
  if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent(outcome ? 'meeting-recording-stop-settled' : 'meeting-recording-manual-stop', {
      detail: { roomId: String(roomId || ''), ...outcome },
    }));
  }
};

// A stop acknowledgement only confirms delivery to the capture browser. Keep
// the action pending until durable completion, including when capture has
// already stopped and only its final upload is running.
export function stopMeetingRecording({ roomId, recorder, socket, api, requestStop, onPhase = () => {},
  eventTarget = typeof window === 'undefined' ? null : window, timeoutMs = 180_000, pollMs = 2000,
  recordingId: knownRecordingId = '', onRecordingId = () => {} }) {
  const id = String(roomId || '').trim();
  if (!id) return Promise.reject(new Error('Room ID is missing.'));
  if (activeStops.has(id)) return activeStops.get(id);
  const operation = (async () => {
    onPhase('stopping');
    const ownerStatus = recorder.getStatus?.();
    if (String(ownerStatus?.roomId || '') === id && ownerStatus?.recordingId) onRecordingId(ownerStatus.recordingId);
    if (eventTarget && typeof CustomEvent === 'function') eventTarget.dispatchEvent(new CustomEvent('meeting-recording-manual-stop', { detail: { roomId: id } }));
    if (recorder.isOwnerForRoom?.(id) || recorder.isSavingForRoom?.(id)) {
      onPhase('saving');
      const result = await recorder.stopIfActive(id);
      if (!result) throw new Error('Recording is saved in this browser and needs upload recovery. Use Resume upload to finish saving.');
      if (result?.status === 'completed') { onPhase('saved'); return result; }
      // Older servers may finish capture before committing storage. Follow the
      // same completion verification as a remote moderator in that case.
      knownRecordingId = result?.recordingId || knownRecordingId;
    }
    return new Promise((resolve, reject) => {
      let recordingId = String(knownRecordingId || '');
      let timer;
      let poll;
      let done = false;
      let checking = false;
      let requested = false;
      const early = [];
      const belongs = payload => String(payload?.roomId || '') === id && Boolean(recordingId) &&
        Boolean(payload?.recordingId) && String(payload.recordingId) === recordingId;
      const cleanup = () => {
        clearTimeout(timer); clearInterval(poll);
        socket?.off?.('recording:uploaded', saved);
        socket?.off?.('recording:upload-failed', failed);
        socket?.off?.('recording:stop-failed', failed);
        socket?.off?.('recording:stopped', stopped);
      };
      const settle = (error, value) => {
        if (done) return;
        done = true; cleanup();
        if (error) reject(error);
        else { onPhase('saved'); resolve(value || { success: true, status: 'completed', recordingId }); }
      };
      const saved = (payload = {}) => {
        if (!requested) { early.push(['saved', payload]); return; }
        if (belongs(payload)) settle(null, { ...payload, status: 'completed' });
      };
      const failed = (payload = {}) => {
        if (!requested) { early.push(['failed', payload]); return; }
        if (belongs(payload)) settle(new Error(payload.error || 'Recording save needs a retry. Keep the recording browser open.'));
      };
      const stopped = (payload = {}) => {
        if (!requested) { early.push(['stopped', payload]); return; }
        if (!belongs(payload)) return;
        if (payload.status === 'failed') failed(payload);
        else onPhase('saving');
      };
      const check = async () => {
        if (done || checking || !recordingId) return;
        checking = true;
        try {
          const { data } = await api.get(`/recordings/${encodeURIComponent(recordingId)}`);
          const item = data?.recording;
          if (item && String(item.roomId || item.room?.roomId || '') === id) {
            if (item.status === 'completed') settle(null, { success: true, recordingId, status: 'completed' });
            else if (['failed', 'deleted'].includes(item.status)) settle(new Error('Recording save needs a retry. Keep the recording browser open.'));
          }
        } catch (error) {
          if ([401, 403, 404].includes(error?.response?.status)) settle(new Error('Recording status could not be verified. Refresh and retry.'));
        } finally { checking = false; }
      };
      socket?.on?.('recording:uploaded', saved);
      socket?.on?.('recording:upload-failed', failed);
      socket?.on?.('recording:stop-failed', failed);
      socket?.on?.('recording:stopped', stopped);
      timer = setTimeout(() => settle(new Error('Recording is still saving. Keep the recording browser open and retry the status check.')), timeoutMs);
      poll = setInterval(() => void check(), pollMs);
      (async () => {
        try {
          const response = await requestStop();
          if (done) return;
          if (!response?.success) throw new Error(response?.error || 'Unable to stop the active recorder.');
          recordingId = String(response.recordingId || recordingId);
          if (recordingId) onRecordingId(recordingId);
          requested = true;
          onPhase('saving');
          if (response.status === 'completed' && response.recordingId) { settle(null, response); return; }
          early.forEach(([kind, payload]) => ({ saved, failed, stopped })[kind](payload));
          await check();
        } catch (error) { settle(error); }
      })();
    });
  })();
  activeStops.set(id, operation);
  operation.then(() => {
    eventTarget?.dispatchEvent?.(new CustomEvent('meeting-recording-stop-settled', { detail: { roomId: id, success: true } }));
  }, error => {
    if (eventTarget && typeof CustomEvent === 'function') eventTarget.dispatchEvent(new CustomEvent('meeting-recording-stop-settled', { detail: { roomId: id, success: false, message: recordingUiError(error) } }));
  }).catch(() => {});
  operation.finally(() => { if (activeStops.get(id) === operation) activeStops.delete(id); }).catch(() => {});
  return operation;
}
